import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import { Consensus } from '../components/questions/QuestionPanel';
import { useAuth } from '../context/AuthContext';
import { Search, Zap, X, ChevronRight, RefreshCw, AlertCircle } from 'lucide-react';

// Debounce hook for search input
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
};

// Loading skeleton component
const EventSkeleton = () => (
  <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-5 animate-pulse">
    <div className="flex flex-col md:flex-row md:items-center gap-4">
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <div className="w-2 h-2 rounded-full bg-slate-700" />
          <div className="h-4 w-16 bg-slate-700 rounded" />
          <div className="h-6 w-24 bg-slate-700 rounded-full" />
        </div>
        <div className="h-6 w-3/4 bg-slate-700 rounded mb-2" />
        <div className="h-4 w-1/3 bg-slate-700 rounded" />
      </div>
      <div className="w-full md:w-72 flex-shrink-0">
        <div className="h-4 w-full bg-slate-700 rounded mb-2" />
        <div className="h-2.5 w-full bg-slate-700 rounded" />
      </div>
    </div>
  </div>
);

const EventsList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  
  // All events (unfiltered) - fetched once and cached
  const [allEvents, setAllEvents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [votedEventIds, setVotedEventIds] = useState(new Set());
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Read URL params only once on mount (using ref to avoid re-reads)
  const initialParamsRef = useRef({
    status: searchParams.get('status') || '',
    category: searchParams.get('category') || '',
    search: searchParams.get('search') || ''
  });
  
  // Filter states - initialized from URL params
  const [statusFilter, setStatusFilter] = useState(initialParamsRef.current.status);
  const [categoryFilter, setCategoryFilter] = useState(initialParamsRef.current.category);
  const [searchTerm, setSearchTerm] = useState(initialParamsRef.current.search);
  
  // Debounced search term (300ms delay)
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  
  // Track if initial data has been loaded
  const dataLoadedRef = useRef(false);

  // Fetch all data function (extracted for retry capability)
  const fetchAllData = useCallback(async () => {
    setError(null);
    setInitialLoading(true);
    
    try {
      // Fetch all data in parallel
      const [eventsRes, categoriesRes, votesRes] = await Promise.all([
        api.get('/events'),
        api.get('/categories'),
        isAuthenticated ? api.get('/votes/my-votes') : Promise.resolve({ data: [] })
      ]);
      
      setAllEvents(eventsRes.data);
      setCategories(categoriesRes.data);
      
      if (isAuthenticated && votesRes.data) {
        const votedIds = new Set(votesRes.data.map(v => v.vote?.event_id || v.event_id));
        setVotedEventIds(votedIds);
      }
      
      dataLoadedRef.current = true;
    } catch (err) {
      console.error('Failed to fetch data:', err);
      setError('Failed to load events. Please check your connection and try again.');
    } finally {
      setInitialLoading(false);
    }
  }, [isAuthenticated]);

  // Fetch data once on mount
  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // Refresh vote data when returning to page (without full reload)
  useEffect(() => {
    if (!isAuthenticated || !dataLoadedRef.current) return;
    
    const refreshVotes = async () => {
      try {
        const response = await api.get('/votes/my-votes');
        const votedIds = new Set(response.data.map(v => v.vote?.event_id || v.event_id));
        setVotedEventIds(votedIds);
      } catch (err) {
        console.error('Failed to refresh votes:', err);
      }
    };
    
    // Refresh votes when window regains focus
    const handleFocus = () => refreshVotes();
    window.addEventListener('focus', handleFocus);
    
    return () => window.removeEventListener('focus', handleFocus);
  }, [isAuthenticated]);

  // Update URL params when filters change (using debounced search)
  useEffect(() => {
    const params = new URLSearchParams();
    if (statusFilter) params.set('status', statusFilter);
    if (categoryFilter) params.set('category', categoryFilter);
    if (debouncedSearchTerm) params.set('search', debouncedSearchTerm);
    setSearchParams(params, { replace: true });
  }, [statusFilter, categoryFilter, debouncedSearchTerm, setSearchParams]);

  // Filter events client-side (instant, no API call) - uses debounced search
  const filteredEvents = useMemo(() => {
    return allEvents.filter(event => {
      // Status filter - "closed" includes both "closed" and "past_event"
      if (statusFilter) {
        if (statusFilter === 'closed') {
          if (event.status !== 'closed' && event.status !== 'past_event') return false;
        } else if (event.status !== statusFilter) {
          return false;
        }
      }
      
      // Category filter
      if (categoryFilter && event.category !== categoryFilter) return false;
      
      // Search filter (uses debounced value for filtering)
      if (debouncedSearchTerm) {
        const search = debouncedSearchTerm.toLowerCase();
        const matchesTitle = event.title?.toLowerCase().includes(search);
        const matchesCompany = event.company_name?.toLowerCase().includes(search);
        const matchesTicker = event.ticker_symbol?.toLowerCase().includes(search);
        const matchesDesc = event.short_description?.toLowerCase().includes(search);
        if (!matchesTitle && !matchesCompany && !matchesTicker && !matchesDesc) return false;
      }
      
      return true;
    });
  }, [allEvents, statusFilter, categoryFilter, debouncedSearchTerm]);

  // Calculate unvoted events for current filter
  const unvotedEvents = useMemo(() => 
    filteredEvents.filter(e => !votedEventIds.has(e.id) && e.status === 'active'),
    [filteredEvents, votedEventIds]
  );
  const unvotedCount = unvotedEvents.length;

  // Calculate category counts from ALL events (not filtered)
  const categoryCounts = useMemo(() => {
    const counts = {};
    categories.forEach(cat => {
      const catEvents = allEvents.filter(e => e.category === cat.name);
      const activeCatEvents = catEvents.filter(e => e.status === 'active');
      const catUnvoted = activeCatEvents.filter(e => !votedEventIds.has(e.id));
      counts[cat.name] = {
        total: catEvents.length,
        unvoted: catUnvoted.length
      };
    });
    return counts;
  }, [allEvents, categories, votedEventIds]);

  // Category color scheme
  const getCategoryColors = (category, isActive = false) => {
    const colors = {
      'Approvals': { 
        bg: isActive ? 'bg-purple-500' : 'bg-purple-500/20', 
        text: isActive ? 'text-white' : 'text-purple-300', 
        border: 'border-purple-500/30',
        hover: 'hover:bg-purple-500/30'
      },
      'Trial Data': { 
        bg: isActive ? 'bg-blue-500' : 'bg-blue-500/20', 
        text: isActive ? 'text-white' : 'text-blue-300', 
        border: 'border-blue-500/30',
        hover: 'hover:bg-blue-500/30'
      },
      'Deals': { 
        bg: isActive ? 'bg-emerald-500' : 'bg-emerald-500/20', 
        text: isActive ? 'text-white' : 'text-emerald-300', 
        border: 'border-emerald-500/30',
        hover: 'hover:bg-emerald-500/30'
      },
      'Earnings': { 
        bg: isActive ? 'bg-amber-500' : 'bg-amber-500/20', 
        text: isActive ? 'text-white' : 'text-amber-300', 
        border: 'border-amber-500/30',
        hover: 'hover:bg-amber-500/30'
      },
      'Commercial': { 
        bg: isActive ? 'bg-cyan-500' : 'bg-cyan-500/20', 
        text: isActive ? 'text-white' : 'text-cyan-300', 
        border: 'border-cyan-500/30',
        hover: 'hover:bg-cyan-500/30'
      },
      'Leadership': { 
        bg: isActive ? 'bg-rose-500' : 'bg-rose-500/20', 
        text: isActive ? 'text-white' : 'text-rose-300', 
        border: 'border-rose-500/30',
        hover: 'hover:bg-rose-500/30'
      },
      'Politics': { 
        bg: isActive ? 'bg-red-500' : 'bg-red-500/20', 
        text: isActive ? 'text-white' : 'text-red-300', 
        border: 'border-red-500/30',
        hover: 'hover:bg-red-500/30'
      },
      'Culture': { 
        bg: isActive ? 'bg-indigo-500' : 'bg-indigo-500/20', 
        text: isActive ? 'text-white' : 'text-indigo-300', 
        border: 'border-indigo-500/30',
        hover: 'hover:bg-indigo-500/30'
      },
    };
    return colors[category] || { 
      bg: isActive ? 'bg-slate-500' : 'bg-slate-500/20', 
      text: isActive ? 'text-white' : 'text-slate-300', 
      border: 'border-slate-500/30',
      hover: 'hover:bg-slate-500/30'
    };
  };

  // Status indicator
  const getStatusDot = (status) => {
    const dots = {
      active: 'bg-emerald-500 shadow-emerald-500/50',
      upcoming: 'bg-amber-500 shadow-amber-500/50',
      closed: 'bg-slate-500 shadow-slate-500/50',
      past_event: 'bg-slate-500 shadow-slate-500/50',
    };
    return dots[status] || dots.closed;
  };

  const getStatusLabel = (status) => {
    const labels = { active: 'Active', upcoming: 'Upcoming', closed: 'Closed', past_event: 'Closed' };
    return labels[status] || status;
  };

  // Handle Speed Vote navigation
  const handleSpeedVote = () => {
    const params = new URLSearchParams();
    if (categoryFilter) params.set('category', categoryFilter);
    if (debouncedSearchTerm) params.set('search', debouncedSearchTerm);
    navigate(`/vote${params.toString() ? '?' + params.toString() : ''}`);
  };

  // Clear all filters
  const clearFilters = () => {
    setStatusFilter('');
    setCategoryFilter('');
    setSearchTerm('');
  };

  // Check if any filters are active (fixed logic)
  const hasActiveFilters = statusFilter !== '' || categoryFilter !== '' || debouncedSearchTerm !== '';

  // Determine empty state message
  const getEmptyStateMessage = () => {
    if (allEvents.length === 0) {
      return {
        title: "No events yet",
        subtitle: "Check back soon for new events to vote on!"
      };
    }
    if (debouncedSearchTerm) {
      return {
        title: `No results for "${debouncedSearchTerm}"`,
        subtitle: "Try adjusting your search terms"
      };
    }
    if (categoryFilter && statusFilter) {
      return {
        title: `No ${statusFilter} events in ${categoryFilter}`,
        subtitle: "Try selecting a different category or status"
      };
    }
    if (categoryFilter) {
      return {
        title: `No events in ${categoryFilter}`,
        subtitle: "Try selecting a different category"
      };
    }
    if (statusFilter) {
      return {
        title: `No ${statusFilter} events`,
        subtitle: "Try selecting a different status filter"
      };
    }
    return {
      title: "No events found",
      subtitle: "Try adjusting your filters"
    };
  };

  return (
    <div className="min-h-screen py-6 sm:py-10">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1
            className="text-4xl md:text-5xl font-extrabold tracking-tight mb-4"
            style={{ fontFamily: 'Manrope, sans-serif' }}
            data-testid="events-page-title"
          >
            All Events
          </h1>
          <p className="text-base text-slate-400">
            Browse and vote on pharmaceutical industry events
          </p>
        </div>

        {/* Error State */}
        {error && (
          <div role="alert" className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-red-400" />
                <p className="text-red-300">{error}</p>
              </div>
              <button
                onClick={fetchAllData}
                className="flex items-center gap-2 min-h-11 px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-300 rounded-lg transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Retry
              </button>
            </div>
          </div>
        )}

        {/* Search Bar + Speed Vote Button */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-300" />
            <input
              type="text"
              aria-label="Search questions, companies, or tickers"
              placeholder="Search events, companies, tickers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-10 py-3 bg-slate-900 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50"
              data-testid="search-input"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 text-slate-300 hover:text-slate-300"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          
          {/* Speed Vote Button */}
          {isAuthenticated && unvotedCount > 0 && (
            <button
              onClick={handleSpeedVote}
              className="flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-semibold rounded-xl transition-all hover:scale-[1.02] shadow-lg shadow-indigo-500/25"
              data-testid="speed-vote-button"
            >
              <Zap className="w-5 h-5" />
              <span>Speed Vote</span>
              <span className="px-2 py-0.5 bg-white/20 rounded-full text-sm">
                {unvotedCount}
              </span>
            </button>
          )}
        </div>

        {/* Category Pills */}
        <div className="mb-6">
          <div className="flex flex-wrap gap-2">
            {/* All Categories Button */}
            <button
              onClick={() => setCategoryFilter('')}
              className={`min-h-11 px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                !categoryFilter 
                  ? 'bg-slate-100 text-slate-900' 
                  : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
              }`}
              data-testid="category-all"
            >
              All
              <span className="ml-1.5 text-xs opacity-70">({allEvents.length})</span>
            </button>
            
            {/* Category Buttons */}
            {categories.map((cat) => {
              const isActive = categoryFilter === cat.name;
              const colors = getCategoryColors(cat.name, isActive);
              const counts = categoryCounts[cat.name] || { total: 0, unvoted: 0 };
              
              return (
                <button
                  key={cat.id}
                  onClick={() => setCategoryFilter(isActive ? '' : cat.name)}
                  className={`min-h-11 px-4 py-2 rounded-full text-sm font-semibold border transition-all ${
                    colors.bg
                  } ${colors.text} ${colors.border} ${!isActive ? colors.hover : ''}`}
                  data-testid={`category-${cat.name.toLowerCase().replace(' ', '-')}`}
                >
                  {cat.name}
                  {isAuthenticated && counts.unvoted > 0 ? (
                    <span className="ml-1.5 text-xs">
                      <span className="opacity-70">({counts.total}</span>
                      <span className="text-yellow-300"> • {counts.unvoted} new</span>
                      <span className="opacity-70">)</span>
                    </span>
                  ) : (
                    <span className="ml-1.5 text-xs opacity-70">({counts.total})</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Status Filter & Clear Filters */}
        <div className="flex flex-wrap items-center gap-4 mb-6">
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-300">Status:</span>
            <div className="flex gap-1 bg-slate-900 rounded-lg p-1">
              {[
                { value: '', label: 'All' },
                { value: 'active', label: 'Active' },
                { value: 'upcoming', label: 'Upcoming' },
                { value: 'closed', label: 'Closed' },
              ].map((option) => (
                <button
                  key={option.value || 'all'}
                  onClick={() => setStatusFilter(option.value)}
                  className={`min-h-11 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    statusFilter === option.value
                      ? 'bg-slate-700 text-white'
                      : 'text-slate-400 hover:text-white'
                  }`}
                  data-testid={`status-filter-${option.value || 'all'}`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="text-sm text-slate-300 hover:text-slate-300 flex items-center gap-1"
              data-testid="clear-filters-btn"
            >
              <X className="w-4 h-4" />
              Clear filters
            </button>
          )}

          <div className="ml-auto text-sm text-slate-300">
            {filteredEvents.length} event{filteredEvents.length !== 1 ? 's' : ''}
            {isAuthenticated && unvotedCount > 0 && (
              <span className="ml-2 text-indigo-400">
                • {unvotedCount} unvoted
              </span>
            )}
          </div>
        </div>

        {/* Active Filter Summary */}
        {(categoryFilter || debouncedSearchTerm) && isAuthenticated && unvotedCount > 0 && (
          <div className="mb-6 p-4 bg-gradient-to-r from-indigo-600/10 to-purple-600/10 border border-indigo-500/20 rounded-xl">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <Zap className="w-5 h-5 text-indigo-400" />
                <div>
                  <p className="text-sm font-medium text-white">
                    {unvotedCount} unvoted event{unvotedCount !== 1 ? 's' : ''} 
                    {categoryFilter && <span className="text-indigo-400"> in {categoryFilter}</span>}
                    {debouncedSearchTerm && <span className="text-indigo-400"> matching &ldquo;{debouncedSearchTerm}&rdquo;</span>}
                  </p>
                  <p className="text-xs text-slate-400">Ready for Speed Voting</p>
                </div>
              </div>
              <button
                onClick={handleSpeedVote}
                className="flex items-center gap-2 min-h-11 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-lg transition-colors"
              >
                Start Voting
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Events List */}
        {initialLoading ? (
          // Loading Skeleton
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <EventSkeleton key={i} />
            ))}
          </div>
        ) : error ? (
          // Error state already shown above, show minimal message here
          <div className="text-center py-12 bg-slate-900/50 rounded-xl border border-slate-800">
            <AlertCircle className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400">Unable to display events</p>
          </div>
        ) : filteredEvents.length === 0 ? (
          // Context-aware empty state
          <div className="text-center py-12 bg-slate-900/50 rounded-xl border border-slate-800">
            <div className="w-12 h-12 bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
              <Search className="w-6 h-6 text-slate-300" />
            </div>
            <p className="text-slate-300 font-medium mb-2">{getEmptyStateMessage().title}</p>
            <p className="text-slate-300 text-sm mb-4">{getEmptyStateMessage().subtitle}</p>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="min-h-11 text-indigo-200 hover:text-indigo-100 text-sm font-medium"
              >
                Clear all filters
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {filteredEvents.map((event) => {
              const hasVoted = votedEventIds.has(event.id);
              const colors = getCategoryColors(event.category);
              
              // Build navigation state to pass filter context
              const navigationState = {};
              if (categoryFilter) navigationState.category = categoryFilter;
              if (debouncedSearchTerm) navigationState.search = debouncedSearchTerm;
              
              return (
                <Link
                  key={event.id}
                  to={`/events/${event.id}`}
                  state={Object.keys(navigationState).length > 0 ? navigationState : undefined}
                  className={`block bg-slate-900/50 border border-slate-800 hover:border-slate-700 rounded-xl p-5 transition-all hover:bg-slate-900 ${
                    event.featured ? 'ring-1 ring-amber-500/30' : ''
                  }`}
                  data-testid={`event-row-${event.id}`}
                >
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    {/* Left: Event Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        {/* Status Dot */}
                        <div className="flex items-center gap-1.5" title={getStatusLabel(event.status)}>
                          <span className={`w-2 h-2 rounded-full shadow-lg ${getStatusDot(event.status)}`} />
                          <span className="text-xs text-slate-300">{getStatusLabel(event.status)}</span>
                        </div>

                        {/* Category Badge */}
                        <span className={`text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full border ${colors.bg} ${colors.text} ${colors.border}`}>
                          {event.category}
                        </span>

                        {/* Voted Indicator */}
                        {isAuthenticated && hasVoted && (
                          <span className="text-xs text-slate-300 bg-slate-800 px-2 py-1 rounded">
                            ✓ Voted
                          </span>
                        )}

                        {/* Featured Badge */}
                        {event.featured && (
                          <span className="text-xs font-medium text-amber-400 bg-amber-500/10 px-2 py-1 rounded border border-amber-500/20">
                            ⭐ Featured
                          </span>
                        )}
                      </div>
                      
                      <h3 className="text-lg font-semibold text-white mb-1 line-clamp-2">
                        {event.title}
                      </h3>
                      <p className="text-sm text-slate-300 font-mono">
                        {event.company_name} {event.ticker_symbol && `(${event.ticker_symbol})`}
                      </p>
                    </div>

                    {/* Right: Outcome Badge + Vote Stats */}
                    <div className="w-full md:w-72 flex-shrink-0">
                      {/* Outcome Badge for closed/past events */}
                      {(event.status === 'closed' || event.status === 'past_event') && event.outcome_winner && (
                        <div className="mb-3">
                          <div className={`inline-flex items-center gap-2 min-h-11 px-3 py-1.5 rounded-lg font-semibold text-sm ${
                            event.outcome_winner === 'A' 
                              ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' 
                              : 'bg-rose-500/20 text-rose-400 border border-rose-500/30'
                          }`}>
                            <span className="text-xs opacity-70">Outcome:</span>
                            {event.outcome_winner === 'A' ? event.option_a_label : event.option_b_label}
                          </div>
                        </div>
                      )}

                      <Consensus event={event} />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default EventsList;
